import { Injectable, inject } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { ApiService } from './api.service';

/**
 * User Group interface
 */
export interface UserGroup {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  userIds: string[];
  createdAt: string;
}

/**
 * Service for user group management API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class UserGroupService {
  private apiService = inject(ApiService);
  private baseUrl: string = '';

  /**
   * Set the base URL for API calls (should be called from app initialization)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get all user groups for a tenant with optional pagination.
   */
  getUserGroups(
    tenantId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    userGroups: UserGroup[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {};
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        userGroups: UserGroup[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/user_groups_api/api/tenants/${tenantId}/userGroups`,
        Object.keys(params).length > 0 ? params : undefined,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get a single user group by ID
   */
  getUserGroup(tenantId: string, groupId: string): Observable<{ userGroup: UserGroup }> {
    return from(
      this.apiService.get<{ userGroup: UserGroup }>(
        this.baseUrl,
        `/user_groups_api/api/tenants/${tenantId}/userGroups/${groupId}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a new user group
   */
  createUserGroup(
    tenantId: string,
    userGroup: Partial<UserGroup>,
  ): Observable<{ userGroup: UserGroup }> {
    return from(
      this.apiService.post<{ userGroup: UserGroup }, Partial<UserGroup>>(
        this.baseUrl,
        `/user_groups_api/api/tenants/${tenantId}/userGroups`,
        userGroup,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update an existing user group
   */
  updateUserGroup(
    tenantId: string,
    groupId: string,
    userGroup: Partial<UserGroup>,
  ): Observable<{ userGroup: UserGroup }> {
    return from(
      this.apiService.put<{ userGroup: UserGroup }, Partial<UserGroup>>(
        this.baseUrl,
        `/user_groups_api/api/tenants/${tenantId}/userGroups/${groupId}`,
        userGroup,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Delete a user group
   */
  deleteUserGroup(tenantId: string, groupId: string): Observable<{ success: boolean }> {
    return from(
      this.apiService.delete<{ success: boolean }>(
        this.baseUrl,
        `/user_groups_api/api/tenants/${tenantId}/userGroups/${groupId}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }
}
